import { buildStyles } from "./styles";
import { appendLinkedText } from "./linkify";
import { detectHostTheme, watchHostTheme } from "./host-theme";
import { detectCountry } from "./country";
import { isShownOn, watchPath } from "./paths";
import { resolveIconSvg } from "./icon-presets";

/** The visitor's IANA timezone, when the browser will tell us. */
function resolveTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
import {
  detectLanguage,
  isRtl,
  normalizeLanguage,
  restrictToEnabled,
  watchLanguage,
  type LanguageCode,
} from "./language";
import { stringsFor, type Strings } from "./i18n";
import type { ChatHistoryResponse, ChatResponse, Citation, HandoffResult, MessageFeedback, WidgetConfig } from "./types";

const CHAT_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
// Mirrors backend/src/business/assistant-name.ts. The greeting names the
// assistant so a renamed one doesn't introduce itself as "Minaya".
const DEFAULT_ASSISTANT_NAME = "Minaya";
/** Max characters a visitor may send. Mirrors MAX_MESSAGE_LENGTH server-side. */
const MAX_INPUT_LENGTH = 500;
/** Show the counter only when the visitor is close to the cap. */
const COUNTER_VISIBLE_AT = 400;
// Default header logo: Minaya's two-stroke symbol (Minaya-Brand-Package,
// Sept 2026). Businesses on a paid plan can replace it with their own image
// (config.widgetLogoUrl); everyone else sees this. A clean vector, so it is
// inlined and drawn in currentColor it takes the header's text colour,
// whatever the business chose, instead of being fixed white.
const MINAYA_MARK_SVG =
  `<svg viewBox="-20 -50 400 400" fill="none" stroke="currentColor" stroke-width="76" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
  `<path d="M39 197V108C39 69 70 39 108 39H270"/><path d="M128 137H263C298 137 320 160 320 194V260"/></svg>`;

const defaultWelcome = (name: string) =>
  `Hi, I am ${name} your AI assistant. How can I help you today?`;

const GREETING_CLOSE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`;
const MINIMIZE_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;
const SEND_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>`;
const THUMBS_UP_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/></svg>`;
const THUMBS_DOWN_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/></svg>`;

/**
 * Captured while the script is still executing: `document.currentScript` is
 * null for async scripts and once DOMContentLoaded fires, so reading it later
 * silently loses the embed attributes.
 */
const EMBED_SCRIPT: HTMLScriptElement | null =
  (document.currentScript as HTMLScriptElement | null) ?? null;

/** Locates our own script tag when currentScript was unavailable. */
function findEmbedScript(): HTMLScriptElement | null {
  if (EMBED_SCRIPT) return EMBED_SCRIPT;
  const candidates = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[data-site-key]'),
  );
  return candidates.find((el) => /widget\.js/i.test(el.src)) ?? candidates[0] ?? null;
}

function getScriptConfig(): { siteKey: string; apiUrl: string } {
  const script = findEmbedScript();
  const siteKey = script?.dataset.siteKey;
  const apiUrl = script?.dataset.apiUrl;
  if (!siteKey) {
    throw new Error(
      "AiChatWidget: missing data-site-key attribute on the embed script tag",
    );
  }
  if (!apiUrl) {
    throw new Error(
      "AiChatWidget: missing data-api-url attribute on the embed script tag",
    );
  }
  return { siteKey, apiUrl: apiUrl.replace(/\/+$/, '') };
}

/**
 * Reads a key, migrating it from the pre-rename "aicb_" name on first sight.
 * The prefix changed from aicb to maicb (Minaya AI chatbot); without this a
 * returning visitor would have lost their conversation on the day it shipped.
 */
function readStorage(storage: Storage, key: string): string | null {
  const value = storage.getItem(key);
  if (value !== null) return value;
  const legacyKey = key.replace(/^maicb_/, "aicb_");
  const legacy = storage.getItem(legacyKey);
  if (legacy === null) return null;
  storage.setItem(key, legacy);
  storage.removeItem(legacyKey);
  return legacy;
}

function getOrCreateVisitorId(): string {
  const key = "maicb_visitor_id";
  let id = readStorage(localStorage, key);
  if (!id) {
    id = typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `v_${Date.now()}_${Math.random()}`;
    localStorage.setItem(key, id);
  }
  return id;
}

function getCurrentOrigin(): string {
  return window.location.origin;
}



function getStoredSessionId(siteKey: string): string | null {
  return readStorage(localStorage, `maicb_session_${siteKey}`);
}

function storeSessionId(siteKey: string, sessionId: string): void {
  localStorage.setItem(`maicb_session_${siteKey}`, sessionId);
}

/**
 * Whether this visitor has already been through the pre-chat form.
 *
 * Deliberately separate from sessionId: a session is created the moment
 * trackPageView() fires, purely for analytics, before the visitor has done
 * anything so "a session exists" is not evidence they ever saw or answered
 * the form. This flag is set only when the form is actually submitted or
 * skipped, so a reload before that keeps showing it.
 */
function hasCompletedPreChat(siteKey: string): boolean {
  return readStorage(localStorage, `maicb_prechat_done_${siteKey}`) === "1";
}

function markPreChatDone(siteKey: string): void {
  localStorage.setItem(`maicb_prechat_done_${siteKey}`, "1");
}

/**
 * An API refusal, with the status kept so the widget can tell a broken
 * embed (401: unknown site key, 403: origin not allowed) from an outage.
 */
class WidgetApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/**
 * fetch() rejects with a TypeError when the request never got a response:
 * the host is down or wrong, DNS failed, or the browser blocked it (CORS).
 * There is no status to read, so this is the only signal available.
 */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

/** True when the embed itself is wrong and nothing the visitor does will help. */
function isConfigurationError(err: unknown): err is WidgetApiError {
  return err instanceof WidgetApiError && (err.status === 401 || err.status === 403);
}

async function fetchWidgetConfig(apiUrl: string, siteKey: string): Promise<WidgetConfig> {
  const res = await fetch(`${apiUrl}/public/widget-config`, {
    headers: { "X-Site-Key": siteKey },
  });
  if (!res.ok) {
    throw new WidgetApiError(
      `AiChatWidget: failed to load widget config (${res.status})`,
      res.status,
    );
  }
  return res.json();
}

async function startChat(
  apiUrl: string,
  siteKey: string,
  body: {
    sessionId?: string;
    visitorName?: string;
    visitorEmail?: string;
    visitorIdentifier?: string;
    origin?: string;
    country?: string;
  },
): Promise<{ sessionId: string }> {
  const res = await fetch(`${apiUrl}/public/chat/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Site-Key": siteKey },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`start failed (${res.status})`);
  return res.json();
}

async function requestHandoff(
  apiUrl: string,
  siteKey: string,
  body: {
    sessionId: string;
    visitorEmail: string;
    visitorName?: string;
    visitorMessage?: string;
  },
): Promise<HandoffResult> {
  const res = await fetch(`${apiUrl}/public/chat/handoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Site-Key": siteKey },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`handoff failed (${res.status})`);
  }
  return res.json();
}

async function fetchChatHistory(
  apiUrl: string,
  siteKey: string,
  sessionId: string,
): Promise<ChatHistoryResponse | null> {
  try {
    const res = await fetch(`${apiUrl}/public/chat/history/${sessionId}`, {
      headers: { "X-Site-Key": siteKey },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function submitFeedback(
  apiUrl: string,
  siteKey: string,
  messageId: string,
  feedback: MessageFeedback,
): Promise<boolean> {
  try {
    const res = await fetch(`${apiUrl}/public/chat/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Site-Key": siteKey },
      body: JSON.stringify({ messageId, feedback }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function loadGoogleFont(fontFamily: string): void {
  if (fontFamily === "inherit" || document.querySelector(`link[href*="${fontFamily}"]`)) {
    return;
  }
  
  // Map common font names to Google Fonts
  const fontMap: Record<string, string> = {
    "Poppins": "Poppins:wght@400;500;600",
    "Roboto": "Roboto:wght@400;500;700",
    "Open Sans": "Open+Sans:wght@400;500;600",
    "Lato": "Lato:wght@400;700",
    "Montserrat": "Montserrat:wght@400;500;600",
    "Inter": "Inter:wght@400;500;600",
  };
  
  const googleFontName = fontMap[fontFamily];
  if (googleFontName) {
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = "https://fonts.googleapis.com";
    document.head.appendChild(link);
    
    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "anonymous";
    document.head.appendChild(link2);
    
    const link3 = document.createElement("link");
    link3.href = `https://fonts.googleapis.com/css2?family=${googleFontName}&display=swap`;
    link3.rel = "stylesheet";
    document.head.appendChild(link3);
  }
}

async function sendChatMessage(
  apiUrl: string,
  siteKey: string,
  message: string,
  sessionId: string | null,
  visitorIdentifier: string,
  origin: string,
  country: string,
  language: string,
  onChunk: (chunk: string) => void,
): Promise<ChatResponse> {
  const res = await fetch(`${apiUrl}/public/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Site-Key": siteKey },
    body: JSON.stringify({
      message,
      sessionId: sessionId ?? undefined,
      language,
      visitorIdentifier: visitorIdentifier,
      origin: origin,
      country: country,
      // Sent so a lead's stated availability ("after 5pm") can be read in
      // the visitor's own zone rather than the server's.
      timezone: resolveTimezone(),
      stream: true // Enable streaming
    }),
  });
  
  if (!res.ok) {
    throw new WidgetApiError(
      `AiChatWidget: chat request failed (${res.status})`,
      res.status,
    );
  }

  // Handle streaming response
  const reader = res.body?.getReader();
  const decoder = new TextDecoder();
  let fullReply = "";
  let finalSessionId = sessionId ?? "";

  if (reader) {
    // SSE frames do not align to network packets: a single "data: {...}" line
    // can arrive split across two reads. Anything after the last newline is
    // therefore an incomplete frame and must be held back until the rest of it
    // arrives, or the two halves each fail to parse and the content is lost.
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // stream: true keeps a multi-byte character that straddles a read
        // boundary intact without it Arabic, Chinese and emoji corrupt.
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        // The trailing element is whatever followed the last newline: either
        // an empty string, or a partial frame still in flight.
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim() === '') continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullReply += parsed.content;
                onChunk(parsed.content);
              }
              if (parsed.sessionId) {
                finalSessionId = parsed.sessionId;
              }
              if (parsed.done) {
                return {
                  sessionId: finalSessionId,
                  reply: fullReply,
                  messageId: parsed.messageId,
                  citations: parsed.citations,
                };
              }
            } catch (e) {
              // A frame that survived buffering and still will not parse is
              // genuinely malformed. Skip it rather than failing the reply.
            }
          }
        }
      }

      // The stream ended without a done frame. Flush anything the decoder is
      // still holding and try the final buffered line, so a reply that arrives
      // complete but unterminated is not thrown away.
      buffer += decoder.decode();
      const tail = buffer.trim();
      if (tail.startsWith('data: ')) {
        try {
          const parsed = JSON.parse(tail.slice(6));
          if (parsed.content) {
            fullReply += parsed.content;
            onChunk(parsed.content);
          }
          if (parsed.sessionId) finalSessionId = parsed.sessionId;
          if (parsed.done) {
            return {
              sessionId: finalSessionId,
              reply: fullReply,
              messageId: parsed.messageId,
              citations: parsed.citations,
            };
          }
        } catch (e) {
          // Truncated tail return what was already streamed.
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  return { sessionId: finalSessionId, reply: fullReply };
}

/**
 * Platform themes and plugins routinely inject the same embed twice  
 * WordPress via a plugin plus a theme header, Shopify via app blocks plus
 * theme.liquid. Without a guard that renders two overlapping widgets.
 */
const WIDGET_FLAG = "__minayaWidgetLoaded";

function init() {
  const w = window as unknown as Record<string, unknown>;
  if (w[WIDGET_FLAG]) return;
  w[WIDGET_FLAG] = true;

  const { siteKey, apiUrl } = getScriptConfig();
  const visitorIdentifier = getOrCreateVisitorId();
  let sessionId = getStoredSessionId(siteKey);
  let country = "unknown";
  let lastUserActivity = Date.now();
  let engagementPopupShown = false;
  // Only a visitor who has actually exchanged a message should ever be asked
  // for feedback opening the widget and leaving it idle is not a
  // conversation. Set once the first message is sent, or immediately for a
  // returning visitor whose history was restored.
  let conversationStarted = false;
  // True while a required pre-chat form is pending. setBusy() only disables
  // the input/send button, but a suggestion chip's click handler calls
  // handleSend() directly rather than clicking that button, so it was able
  // to send a message straight past an unanswered pre-chat form. Checked
  // inside handleSend() itself so every entry point (send button, Enter key,
  // a suggestion chip) is covered by one guard.
  let chatLocked = false;

  // Resolved synchronously from the browser timezone no network request,
  // so it is ready before the first tracking call rather than racing it.
  country = detectCountry();

  // Engagement popup questions
  const engagementQuestions = [
    "How's your experience with our chat?",
    "Is this conversation helpful?",
    "Did you find what you were looking for?",
    "Would you like to continue this conversation?"
  ];
  let currentQuestionIndex = 0;

  // Track widget click/open
  async function trackWidgetClick() {
    try {
      const response = await fetch(`${apiUrl}/public/track-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Site-Key": siteKey },
        body: JSON.stringify({
          sessionId: sessionId ?? undefined,
        language,
          visitorIdentifier: visitorIdentifier,
          origin: getCurrentOrigin(),
          country: country,
        }),
      });
      const data = await response.json();
      if (data.sessionId) {
        sessionId = data.sessionId;
        storeSessionId(siteKey, sessionId);
      }
    } catch (e) {
      // Ignore tracking errors
    }
  }

  // Track page view when widget loads
  async function trackPageView() {
    try {
      const response = await fetch(`${apiUrl}/public/track-page-view`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Site-Key": siteKey },
        body: JSON.stringify({
          sessionId: sessionId ?? undefined,
        language,
          visitorIdentifier: visitorIdentifier,
          origin: getCurrentOrigin(),
          country: country,
        }),
      });
      const data = await response.json();
      if (data.sessionId) {
        sessionId = data.sessionId;
        storeSessionId(siteKey, sessionId);
      }
    } catch (e) {
      // Ignore tracking errors
    }
  }

  // Resolved from the page once config arrives; English until then.
  let language: LanguageCode = "en-GB";
  let t: Strings = stringsFor(language);

  const host = document.createElement("div");
  host.id = "maicb-widget-host";
  // WordPress dark-mode plugins invert colours across the page. The widget
  // themes itself, so these opt it out of a second inversion that would undo
  // the customer's own palette. Harmless when the plugins are absent.
  host.className =
    "wp-dark-mode-ignore darklup-dark-ignore darkluplite-dark-ignore drdt-ignore-dark";
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  shadow.appendChild(styleEl);

  const root = document.createElement("div");
  root.className = "maicb-root";
  shadow.appendChild(root);

  const launcher = document.createElement("button");
  launcher.className = "maicb-launcher";
  launcher.innerHTML = CHAT_ICON_SVG;
  launcher.setAttribute("aria-label", t.openChat);
  root.appendChild(launcher);

  // Applies the business's chosen launcher icon. A custom image always wins
  // over a preset; falls back to the default chat bubble otherwise.
  function applyLauncherIcon(iconUrl: string | null | undefined, iconPreset: string | null | undefined) {
    if (iconUrl) {
      const img = document.createElement("img");
      img.className = "maicb-launcher-icon";
      img.src = iconUrl;
      img.alt = "";
      launcher.replaceChildren(img);
    } else {
      // A constant string, never operator input.
      launcher.innerHTML = resolveIconSvg(iconPreset);
    }
  }

  // Sits above the launcher until the widget is opened. Dismissible, and the
  // dismissal is remembered so it does not nag on every page of a visit.
  const greeting = document.createElement("div");
  greeting.className = "maicb-greeting";
  greeting.hidden = true;

  const greetingText = document.createElement("button");
  greetingText.className = "maicb-greeting-text";
  greetingText.type = "button";
  greeting.appendChild(greetingText);

  const greetingClose = document.createElement("button");
  greetingClose.className = "maicb-greeting-close";
  greetingClose.type = "button";
  greetingClose.innerHTML = GREETING_CLOSE_SVG;
  greeting.appendChild(greetingClose);
  root.appendChild(greeting);

  const GREETING_DISMISSED = `maicb_greeting_${siteKey}`;
  const hideGreeting = (remember: boolean) => {
    greeting.hidden = true;
    if (remember) {
      try {
        sessionStorage.setItem(GREETING_DISMISSED, "1");
      } catch {
        // Private browsing; the greeting simply reappears next page.
      }
    }
  };

  greetingText.addEventListener("click", () => {
    hideGreeting(true);
    launcher.click();
  });
  greetingClose.addEventListener("click", (event) => {
    event.stopPropagation();
    hideGreeting(true);
  });

  const panel = document.createElement("div");
  panel.className = "maicb-panel";
  root.appendChild(panel);

  const header = document.createElement("div");
  header.className = "maicb-header";
  panel.appendChild(header);

  const minimizeButton = document.createElement("button");
  minimizeButton.className = "maicb-minimize";
  minimizeButton.type = "button";
  minimizeButton.innerHTML = MINIMIZE_ICON_SVG;
  minimizeButton.setAttribute("aria-label", t.minimise);
  minimizeButton.addEventListener("click", () => {
    panel.classList.remove("maicb-open");
  });

  const messages = document.createElement("div");
  messages.className = "maicb-messages";
  panel.appendChild(messages);

  // One-click prompts, above the composer. Hidden once the visitor sends
  // anything they exist to help someone start, and would only crowd the
  // transcript afterwards.
  const suggestions = document.createElement("div");
  suggestions.className = "maicb-suggestions";
  suggestions.hidden = true;
  panel.appendChild(suggestions);

  const footer = document.createElement("div");
  footer.className = "maicb-footer";
  panel.appendChild(footer);

  // Sits below the composer. Built as nodes rather than innerHTML, and
  // hidden until the config says this plan shows it.
  const branding = document.createElement("div");
  branding.className = "maicb-branding";
  branding.hidden = true;
  const brandingLink = document.createElement("a");
  brandingLink.className = "maicb-branding-link";
  brandingLink.href = "https://minaya.ai";
  brandingLink.target = "_blank";
  brandingLink.rel = "noopener noreferrer";
  brandingLink.textContent = "Powered by minaya.ai";
  branding.appendChild(brandingLink);
  panel.appendChild(branding);

  const input = document.createElement("input");
  input.className = "maicb-input";
  input.type = "text";
  input.placeholder = t.typeMessage;
  // Native cap: also truncates pasted text, so nothing oversized is sent.
  input.maxLength = MAX_INPUT_LENGTH;
  footer.appendChild(input);

  // counter sits between input and send
  const counter = document.createElement("span");
  counter.className = "maicb-counter";
  footer.appendChild(counter);

  function updateCounter() {
    const used = input.value.length;
    if (used >= COUNTER_VISIBLE_AT) {
      counter.textContent = `${used}/${MAX_INPUT_LENGTH}`;
      counter.classList.toggle("maicb-counter-max", used >= MAX_INPUT_LENGTH);
      counter.style.display = "block";
    } else {
      counter.style.display = "none";
    }
  }
  updateCounter();
  input.addEventListener("input", updateCounter);

  const sendButton = document.createElement("button");
  sendButton.className = "maicb-send";
  sendButton.innerHTML = SEND_ICON_SVG;
  sendButton.setAttribute("aria-label", t.sendMessage);
  footer.appendChild(sendButton);

  /**
   * Pins the conversation to the newest message.
   *
   * Guards on offsetParent: while the panel is closed it is display:none, and
   * a hidden element reports scrollHeight 0, so the assignment would be a
   * silent no-op. Callers that run before the panel opens rely on the open
   * handler to scroll instead.
   */
  function scrollToLatest() {
    if (!messages.offsetParent) return;
    messages.scrollTop = messages.scrollHeight;
  }

  function addBubble(role: "user" | "assistant" | "error", text: string) {
    const bubble = document.createElement("div");
    bubble.className = `maicb-bubble maicb-bubble-${role}`;
    appendLinkedText(bubble, text);
    messages.appendChild(bubble);
    scrollToLatest();
    return bubble;
  }

  let handoffEnabled = false;
  let handoffButtonLabel = t.talkToHuman;
  let handoffFormOpen = false;
  /** Consecutive replies that answered nothing, used to offer help proactively. */
  let unresolvedStreak = 0;

  /**
   * Inline escape hatch to a person. Only rendered when the business enabled
   * handoff and configured a destination.
   */
  /**
   * Optional pre-chat form. Rendered instead of the welcome message so the
   * visitor sees one thing at a time, and the composer stays disabled until
   * it is dealt with otherwise they could type past it and the business
   * would never get the details it asked for.
   */
  function addPreChatForm(
    config: WidgetConfig,
    onDone: () => void,
  ) {
    const form = document.createElement("form");
    form.className = "maicb-prechat";

    const title = document.createElement("div");
    title.className = "maicb-prechat-title";
    title.textContent = config.preChatTitle;
    form.appendChild(title);

    if (config.preChatMessage) {
      const intro = document.createElement("div");
      intro.className = "maicb-prechat-message";
      intro.textContent = config.preChatMessage;
      form.appendChild(intro);
    }

    const nameInput = document.createElement("input");
    nameInput.className = "maicb-prechat-input";
    nameInput.type = "text";
    nameInput.maxLength = 120;
    nameInput.placeholder = t.preChatName;
    nameInput.setAttribute("aria-label", t.preChatName);
    form.appendChild(nameInput);

    const emailInput = document.createElement("input");
    emailInput.className = "maicb-prechat-input";
    emailInput.type = "email";
    emailInput.required = config.preChatRequireEmail;
    emailInput.placeholder = t.preChatEmail;
    emailInput.setAttribute("aria-label", t.preChatEmail);
    form.appendChild(emailInput);

    const row = document.createElement("div");
    row.className = "maicb-prechat-row";

    const submit = document.createElement("button");
    submit.className = "maicb-prechat-submit";
    submit.type = "submit";
    submit.textContent = t.preChatStart;
    row.appendChild(submit);

    // An optional form needs a way past it, or it is not optional.
    if (!config.preChatRequireEmail) {
      const skip = document.createElement("button");
      skip.className = "maicb-prechat-skip";
      skip.type = "button";
      skip.textContent = t.preChatSkip;
      skip.addEventListener("click", () => {
        markPreChatDone(siteKey);
        form.remove();
        onDone();
      });
      row.appendChild(skip);
    }
    form.appendChild(row);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      submit.disabled = true;
      try {
        const result = await startChat(apiUrl, siteKey, {
          sessionId: sessionId ?? undefined,
          visitorName: nameInput.value.trim() || undefined,
          visitorEmail: emailInput.value.trim() || undefined,
          visitorIdentifier,
          origin: getCurrentOrigin(),
          country,
        });
        sessionId = result.sessionId;
        storeSessionId(siteKey, sessionId);
      } catch {
        // Never trap the visitor behind a failed form their details are a
        // convenience for the business, not a precondition for support.
      } finally {
        markPreChatDone(siteKey);
        submit.disabled = false;
        form.remove();
        onDone();
      }
    });

    messages.appendChild(form);
    setBusy(true);
    chatLocked = true;
    nameInput.focus();
  }

  function addHandoffForm() {
    if (handoffFormOpen) return;
    handoffFormOpen = true;

    const form = document.createElement("form");
    form.className = "maicb-handoff-form";

    const prompt = document.createElement("div");
    prompt.className = "maicb-handoff-prompt";
    prompt.textContent = t.handoffPrompt;
    form.appendChild(prompt);

    const emailInput = document.createElement("input");
    emailInput.className = "maicb-handoff-input";
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.placeholder = t.emailPlaceholder;
    emailInput.setAttribute("aria-label", t.emailLabel);
    form.appendChild(emailInput);

    const noteInput = document.createElement("input");
    noteInput.className = "maicb-handoff-input";
    noteInput.type = "text";
    noteInput.maxLength = 2000;
    noteInput.placeholder = t.detailsPlaceholder;
    noteInput.setAttribute("aria-label", t.detailsLabel);
    form.appendChild(noteInput);

    const row = document.createElement("div");
    row.className = "maicb-handoff-row";

    const submit = document.createElement("button");
    submit.className = "maicb-handoff-submit";
    submit.type = "submit";
    submit.textContent = t.send;
    row.appendChild(submit);

    const cancel = document.createElement("button");
    cancel.className = "maicb-handoff-cancel";
    cancel.type = "button";
    cancel.textContent = t.cancel;
    row.appendChild(cancel);
    form.appendChild(row);

    const error = document.createElement("div");
    error.className = "maicb-handoff-error";
    form.appendChild(error);

    cancel.addEventListener("click", () => {
      handoffFormOpen = false;
      form.remove();
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const email = emailInput.value.trim();
      if (!email) return;

      submit.disabled = true;
      cancel.disabled = true;
      error.textContent = "";
      // The offer only appears after a reply, so a session should exist  
      // but never post without one rather than trusting that invariant.
      if (!sessionId) {
        error.textContent = t.sendFirst;
        submit.disabled = false;
        cancel.disabled = false;
        return;
      }

      try {
        const result = await requestHandoff(apiUrl, siteKey, {
          sessionId,
          visitorEmail: email,
          visitorMessage: noteInput.value.trim() || undefined,
        });
        handoffFormOpen = false;
        form.remove();
        addBubble("assistant", result.message);
      } catch {
        error.textContent =
          t.handoffError;
        submit.disabled = false;
        cancel.disabled = false;
      }
    });

    messages.appendChild(form);
    scrollToLatest();
    emailInput.focus();
  }

  function addHandoffButton(label: string) {
    const wrap = document.createElement("div");
    wrap.className = "maicb-handoff-offer";

    const button = document.createElement("button");
    button.className = "maicb-handoff-btn";
    button.type = "button";
    button.textContent = label;
    button.addEventListener("click", () => {
      wrap.remove();
      addHandoffForm();
    });

    wrap.appendChild(button);
    messages.appendChild(wrap);
    scrollToLatest();
  }

  function addCitations(citations: Citation[]) {
    if (!citations.length) return null;

    const wrap = document.createElement("div");
    wrap.className = "maicb-citations";

    const label = document.createElement("span");
    label.className = "maicb-citations-label";
    label.textContent = citations.length === 1 ? t.source : t.sources;
    wrap.appendChild(label);

    for (const citation of citations) {
      const link = document.createElement("a");
      link.className = "maicb-citation";
      link.href = citation.url;
      link.target = "_blank";
      // noopener keeps the opened page from reaching back via window.opener.
      link.rel = "noopener noreferrer";
      link.textContent = citation.title;
      link.title = citation.url;
      wrap.appendChild(link);
    }

    messages.appendChild(wrap);
    scrollToLatest();
    return wrap;
  }

  function addFeedbackControls(messageId: string, initialFeedback: "up" | "down" | null = null) {
    const wrap = document.createElement("div");
    wrap.className = "maicb-feedback";

    const upBtn = document.createElement("button");
    upBtn.className = "maicb-feedback-btn";
    upBtn.innerHTML = THUMBS_UP_SVG;
    upBtn.setAttribute("aria-label", t.helpful);
    upBtn.type = "button";

    const downBtn = document.createElement("button");
    downBtn.className = "maicb-feedback-btn";
    downBtn.innerHTML = THUMBS_DOWN_SVG;
    downBtn.setAttribute("aria-label", t.notHelpful);
    downBtn.type = "button";

    function applyState(state: "up" | "down" | null) {
      upBtn.classList.toggle("maicb-feedback-active-up", state === "up");
      downBtn.classList.toggle("maicb-feedback-active-down", state === "down");
    }
    applyState(initialFeedback);

    async function rate(value: "up" | "down") {
      upBtn.disabled = true;
      downBtn.disabled = true;
      const ok = await submitFeedback(apiUrl, siteKey, messageId, value);
      if (ok) applyState(value);
      upBtn.disabled = false;
      downBtn.disabled = false;
    }

    upBtn.addEventListener("click", () => rate("up"));
    downBtn.addEventListener("click", () => rate("down"));

    wrap.appendChild(upBtn);
    wrap.appendChild(downBtn);
    messages.appendChild(wrap);
    scrollToLatest();
    return wrap;
  }

  function addTypingIndicator() {
    const indicator = document.createElement("div");
    indicator.className = "maicb-bubble maicb-bubble-assistant maicb-typing";
    indicator.innerHTML = `<span class="maicb-typing-dot"></span><span class="maicb-typing-dot"></span><span class="maicb-typing-dot"></span>`;
    messages.appendChild(indicator);
    scrollToLatest();
    return indicator;
  }

  function removeTypingIndicator(indicator: HTMLElement) {
    indicator.remove();
  }

  function updateBubble(bubble: HTMLElement, text: string) {
    bubble.replaceChildren();
    appendLinkedText(bubble, text);
    scrollToLatest();
  }

  function setBusy(busy: boolean) {
    input.disabled = busy;
    sendButton.disabled = busy;
  }

  /**
   * The API never answered. Usually a wrong data-api-url or a server that
   * is not running say which URL was tried, so the installer can see the
   * mistake without opening devtools. Not locked: once the server is back,
   * the next message goes through.
   */
  function showUnreachableError() {
    const pageLanguage = detectLanguage(language);
    const strings = stringsFor(pageLanguage);
    root.setAttribute("dir", isRtl(pageLanguage) ? "rtl" : "ltr");
    root.setAttribute("lang", pageLanguage);
    const bubble = addBubble("assistant", strings.apiUnreachable(apiUrl));
    bubble.className = "maicb-bubble maicb-bubble-error";
    console.error(
      `Minaya widget: could not reach the API at ${apiUrl}. Check data-api-url in the embed script and that the server is running.`,
    );
  }

  /**
   * The embed is wrong an unknown site key, or a domain the widget does
   * not allow. Say so where the installer will look (the chat itself, in
   * the page's language) and in the console, then lock the composer: every
   * message would fail the same way.
   */
  function showConfigurationError(err: WidgetApiError) {
    const pageLanguage = detectLanguage(language);
    const strings = stringsFor(pageLanguage);
    const text = err.status === 403 ? strings.originNotAllowed : strings.invalidSiteKey;
    root.setAttribute("dir", isRtl(pageLanguage) ? "rtl" : "ltr");
    root.setAttribute("lang", pageLanguage);
    input.placeholder = strings.typeMessage;
    hideSuggestions();
    const bubble = addBubble("assistant", text);
    bubble.className = "maicb-bubble maicb-bubble-error";
    chatLocked = true;
    setBusy(true);
    console.error(
      err.status === 403
        ? `Minaya widget: the origin ${getCurrentOrigin()} is not in this widget's allowed origins. Add it under Widget settings in the Minaya dashboard.`
        : `Minaya widget: the site key "${siteKey}" is not recognised. Copy the current key from the Embed page of the Minaya dashboard.`,
    );
  }

  // Engagement popup functions
  function showEngagementPopup() {
    if (engagementPopupShown || currentQuestionIndex >= engagementQuestions.length) return;
    
    const question = engagementQuestions[currentQuestionIndex];
    const popup = addBubble("assistant", question);
    engagementPopupShown = true;
    
    // Store popup reference for potential removal
    (popup as any).isEngagementPopup = true;
    
    // Auto-advance to next question after 30 seconds if no response
    setTimeout(() => {
      if ((popup as any).isEngagementPopup && currentQuestionIndex < engagementQuestions.length - 1) {
        currentQuestionIndex++;
        engagementPopupShown = false;
        showEngagementPopup();
      }
    }, 30000);
  }

  function checkUserInactivity() {
    const now = Date.now();
    const inactiveTime = now - lastUserActivity;

    // Only once a conversation has actually started, and after 3 minutes of
    // inactivity a visitor who never sent a message has nothing to give
    // feedback on.
    if (
      conversationStarted &&
      inactiveTime > 180000 &&
      !engagementPopupShown &&
      panel.classList.contains("maicb-open")
    ) {
      showEngagementPopup();
    }
  }

  function updateUserActivity() {
    lastUserActivity = Date.now();
    // Reset engagement popup if user responds
    if (engagementPopupShown) {
      currentQuestionIndex++;
      engagementPopupShown = false;
      // Show next question if available
      if (currentQuestionIndex < engagementQuestions.length) {
        setTimeout(showEngagementPopup, 5000);
      }
    }
  }

  /** Visitor explicitly asking for a person, in the usual phrasings. */
  const HUMAN_REQUEST =
    /\b(?:talk|speak|chat|connect|put)\s+(?:me\s+|us\s+)?(?:through\s+)?(?:to|with)\s+(?:a|an|the\s+)?\s*(?:human|person|agent|someone|somebody|rep\b|representative|real\s+(?:person|human))|\b(?:human|live|real)\s+(?:agent|support|person)\b|\bcontact\s+(?:support|sales|your\s+team|a\s+human)\b/i;

  /**
   * Offers the escape hatch when the visitor asked for a person, or when the
   * bot has failed to answer twice running. Citations are the signal for the
   * latter: a grounded reply carries sources, an ungrounded one does not.
   */
  function maybeOfferHandoff(userText: string, grounded: boolean) {
    if (!handoffEnabled || handoffFormOpen) return;

    if (HUMAN_REQUEST.test(userText)) {
      unresolvedStreak = 0;
      addHandoffForm();
      return;
    }

    unresolvedStreak = grounded ? 0 : unresolvedStreak + 1;
    if (unresolvedStreak >= 2) {
      unresolvedStreak = 0;
      addHandoffButton(handoffButtonLabel);
    }
  }

  /** Removes the prompts for good; the conversation has started. */
  function hideSuggestions() {
    if (!suggestions.hidden) {
      suggestions.hidden = true;
      suggestions.replaceChildren();
    }
  }

  async function handleSend() {
    if (chatLocked) return;
    const text = input.value.trim();
    if (!text) return;
    hideSuggestions();
    input.value = "";
    updateCounter();
    conversationStarted = true;
    updateUserActivity(); // Update activity on user message
    addBubble("user", text);
    setBusy(true);

    // Show typing indicator
    const typingIndicator = addTypingIndicator();

    try {
      let fullResponse = "";
      let currentResponseBubble: HTMLElement | null = null;

      const response = await sendChatMessage(apiUrl, siteKey, text, sessionId, visitorIdentifier, getCurrentOrigin(), country, language, (chunk) => {
        // Remove typing indicator on first chunk
        if (typingIndicator.parentNode) {
          removeTypingIndicator(typingIndicator);
        }
        fullResponse += chunk;
        // Create or update the current response bubble
        if (!currentResponseBubble) {
          currentResponseBubble = addBubble("assistant", fullResponse);
        } else {
          updateBubble(currentResponseBubble, fullResponse);
        }
      });
      sessionId = response.sessionId;
      storeSessionId(siteKey, sessionId);
      if (response.citations?.length) {
        addCitations(response.citations);
      }
      if (response.messageId) {
        addFeedbackControls(response.messageId);
      }
      maybeOfferHandoff(text, Boolean(response.citations?.length));
    } catch (err) {
      removeTypingIndicator(typingIndicator);
      if (isConfigurationError(err)) {
        showConfigurationError(err);
        return;
      }
      if (isNetworkError(err)) {
        showUnreachableError();
        return;
      }
      const errorBubble = addBubble("assistant", t.genericError);
      errorBubble.className = "maicb-bubble maicb-bubble-error";
    } finally {
      if (!chatLocked) setBusy(false);
      scrollToLatest();
    }
  }

  sendButton.addEventListener("click", handleSend);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") handleSend();
  });
  input.addEventListener("input", updateUserActivity); // Track typing activity
  launcher.addEventListener("click", () => {
    hideGreeting(true);
    panel.classList.toggle("maicb-open");
    updateUserActivity(); // Update activity when opening widget
    if (panel.classList.contains("maicb-open")) {
      trackWidgetClick();
      // First moment the panel has a measurable height, so this is where a
      // restored conversation actually gets pinned to the bottom.
      scrollToLatest();
    }
  });

  // Check for user inactivity every 10 seconds
  setInterval(checkUserInactivity, 10000);

  // Track initial page view when widget loads
  trackPageView();

  /** Resolves to whether the visitor had a prior conversation to restore. */
  async function restoreHistoryOrWelcome(welcomeMessage: string): Promise<boolean> {
    if (sessionId) {
      const history = await fetchChatHistory(apiUrl, siteKey, sessionId);
      if (history && history.messages.length > 0) {
        for (const msg of history.messages) {
          addBubble(msg.role, msg.content);
          if (msg.role === "assistant") {
            addFeedbackControls(msg.id, msg.feedback);
          }
        }
        // The panel may have opened while this was in flight.
        requestAnimationFrame(scrollToLatest);
        conversationStarted = true;
        return true;
      }
    }
    addBubble("assistant", welcomeMessage);
    return false;
  }

  // Only before the first message: a visitor who has already started a
  // conversation has history to show instead, so the prompts would be noise.
  // A tracking sessionId alone is not evidence of that page-view tracking
  // creates one before any message is ever sent so this is driven by
  // whether restoreHistoryOrWelcome actually found history, not by session
  // presence.
  function renderSuggestions(prompts: string[]) {
    for (const question of prompts.slice(0, 4)) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "maicb-suggestion";
      chip.textContent = question;
      chip.addEventListener("click", () => {
        // Route through the input so it takes the same path as typing  
        // one send function, one set of guards.
        input.value = question;
        void handleSend();
      });
      suggestions.appendChild(chip);
    }
    suggestions.hidden = false;
  }

  fetchWidgetConfig(apiUrl, siteKey)
    .then((config) => {
      // Where the business wants the widget. Hidden rather than removed, so
      // a single-page app that navigates onto an allowed page gets it back
      // with the conversation intact.
      const applyVisibility = () => {
        const shown = isShownOn(
          window.location.pathname,
          config.widgetPathMode ?? "all",
          config.widgetPathRules ?? [],
        );
        host.hidden = !shown;
        host.style.display = shown ? "" : "none";
      };
      applyVisibility();
      watchPath(applyVisibility);

      // Load Google Font if needed
      loadGoogleFont(config.widgetFontFamily);

      branding.hidden = !config.showBranding;

      const prompts = (config.suggestedQuestions ?? []).filter((q: string) => q.trim());

      let dismissed = false;
      try {
        dismissed = readStorage(sessionStorage, GREETING_DISMISSED) === "1";
      } catch {
        // Storage unavailable; treat as not dismissed.
      }
      greeting.hidden =
        !config.greetingEnabled || dismissed || config.widgetOpenByDefault;

      handoffEnabled = config.handoffEnabled;
      handoffButtonLabel = config.handoffButtonLabel || handoffButtonLabel;

      const isLeft = config.widgetPosition === "bottom_left";

      // "auto" follows the host page. Resolving it to a concrete palette here,
      // rather than leaving it to a media query, is what lets the widget track
      // a site's own light/dark toggle prefers-color-scheme only sees the OS.
      const applyStyles = (resolvedMode: "light" | "dark") => {
        styleEl.textContent = buildStylesFor(resolvedMode);
      };

      const buildStylesFor = (mode: "light" | "dark") =>
        buildStyles(
        config.widgetPrimaryColor,
        isLeft,
        config.widgetFontFamily,
        config.widgetCustomCss,
        {
          headerTextColor: config.widgetHeaderTextColor,
          botBubbleColor: config.widgetBotBubbleColor,
          botTextColor: config.widgetBotTextColor,
          userTextColor: config.widgetUserTextColor,
        },
        // Dark values fall back to the built-in dark palette when unset, so a
        // customer who only picks light colours still gets a usable dark mode.
        {
          primaryColor: config.widgetDarkPrimaryColor || undefined,
          headerTextColor: config.widgetDarkHeaderTextColor || undefined,
          botBubbleColor: config.widgetDarkBotBubbleColor || undefined,
          botTextColor: config.widgetDarkBotTextColor || undefined,
          userTextColor: config.widgetDarkUserTextColor || undefined,
          surfaceColor: config.widgetDarkSurfaceColor || undefined,
          panelColor: config.widgetDarkSurfaceColor || undefined,
          textColor: config.widgetDarkTextColor || undefined,
        },
        mode,
      );

          // Language: the business's default is only a fallback what the page
      // declares wins, so one widget serves a multilingual site correctly.
      /** The greeting for a language: an override, else the shared text. */
      const greetingFor = (lang: LanguageCode): string => {
        const map = config.greetingTranslations;
        const exact = map?.[lang];
        if (exact?.trim()) return exact;
        const base = lang.split("-")[0];
        const sameBase = map
          ? Object.keys(map).find((k) => k.split("-")[0] === base)
          : undefined;
        if (sameBase && map?.[sameBase]?.trim()) return map[sameBase];
        return config.greetingText;
      };

      const applyLanguage = (next: LanguageCode) => {
        language = next;
        t = stringsFor(next);

        launcher.setAttribute("aria-label", t.openChat);
        greetingText.textContent = greetingFor(next);
        greetingClose.setAttribute("aria-label", t.dismissGreeting);
        input.placeholder = t.typeMessage;
        sendButton.setAttribute("aria-label", t.sendMessage);
        minimizeButton.setAttribute("aria-label", t.minimise);

        // Arabic reverses the whole layout, not just the text.
        const dir = isRtl(next) ? "rtl" : "ltr";
        root.setAttribute("dir", dir);
        root.setAttribute("lang", next);
      };

      /** The greeting for a language: an override, else the single field. */
      const welcomeFor = (lang: LanguageCode): string => {
        const map = config.widgetWelcomeTranslations;
        const exact = map?.[lang];
        if (exact?.trim()) return exact;
        const base = lang.split("-")[0];
        const sameBase = map
          ? Object.keys(map).find((k) => k.split("-")[0] === base)
          : undefined;
        if (sameBase && map?.[sameBase]?.trim()) return map[sameBase];
        return config.widgetWelcomeMessage || defaultWelcome(assistantName);
      };

      const configuredLanguage = normalizeLanguage(
        config.widgetDefaultLanguage,
        "en-GB",
      );
      const enabled = config.widgetLanguages ?? [configuredLanguage];
      const resolve = (detected: LanguageCode) =>
        restrictToEnabled(detected, enabled, configuredLanguage);

      applyLanguage(resolve(detectLanguage(configuredLanguage)));
      watchLanguage(configuredLanguage, (next) => applyLanguage(resolve(next)));

  const configuredMode = config.widgetThemeMode ?? "light";
      if (configuredMode === "auto") {
        // Resolve now, then follow the page for as long as the widget lives.
        applyStyles(detectHostTheme());
        watchHostTheme((theme) => applyStyles(theme));
      } else {
        applyStyles(configuredMode);
      }

      applyLauncherIcon(config.widgetIconUrl, config.widgetIconPreset);

      // Header: logo (or the default icon) plus the assistant's name.
      // Built as nodes so the operator-supplied name is never parsed as HTML.
      header.replaceChildren();
      if (config.widgetLogoUrl) {
        const logo = document.createElement("img");
        logo.className = "maicb-logo";
        logo.src = config.widgetLogoUrl;
        logo.alt = "";
        header.appendChild(logo);
      } else {
        const icon = document.createElement("span");
        icon.className = "maicb-logo-mark";
        // A constant string, never operator input.
        icon.innerHTML = MINAYA_MARK_SVG;
        header.appendChild(icon);
      }
      const headerTitle = document.createElement("span");
      headerTitle.className = "maicb-header-title";
      const assistantName =
        config.widgetAssistantName || DEFAULT_ASSISTANT_NAME;
      headerTitle.textContent = assistantName;
      header.appendChild(headerTitle);
      // Rebuilding the header wipes its children, so reattach the control.
      header.appendChild(minimizeButton);

      // Only ask a visitor who has not already been through it. Gated on
      // hasCompletedPreChat, not sessionId a session exists as soon as
      // trackPageView() fires, before the visitor has done anything, so
      // session presence alone would skip the form on a reload before it was
      // ever answered.
      const showPreChat = config.preChatEnabled && !hasCompletedPreChat(siteKey);
      if (showPreChat) {
        addPreChatForm(config, () => {
          setBusy(false);
          chatLocked = false;
          void restoreHistoryOrWelcome(welcomeFor(language)).then((hadHistory) => {
            if (!hadHistory && prompts.length > 0) renderSuggestions(prompts);
          });
        });
      } else {
        void restoreHistoryOrWelcome(welcomeFor(language)).then((hadHistory) => {
          if (!hadHistory && prompts.length > 0) renderSuggestions(prompts);
        });
      }

      // Auto-open after the config has applied, so the panel never flashes
      // with default styling before the business's colours are in place.
      if (config.widgetOpenByDefault && !host.hidden && !panel.classList.contains("maicb-open")) {
        panel.classList.add("maicb-open");
        trackWidgetClick();
        // Auto-open scrolls too. History may still be loading, so this runs
        // after paint and restoreHistoryOrWelcome scrolls again when it
        // finishes, by which point the panel is measurable.
        requestAnimationFrame(scrollToLatest);
      }
    })
    .catch((err: unknown) => {
      styleEl.textContent = buildStyles("#075CE5", false, "inherit", null);
      header.innerHTML = `<span class="maicb-logo-mark">${MINAYA_MARK_SVG}</span><span class="maicb-header-title"></span>`;
      const fallbackTitle = header.querySelector(".maicb-header-title");
      if (fallbackTitle) fallbackTitle.textContent = DEFAULT_ASSISTANT_NAME;
      if (isConfigurationError(err)) {
        // Not a welcome and no history: the only useful thing to show is
        // what is wrong. Open the panel so the installer sees it at once.
        showConfigurationError(err);
        panel.classList.add("maicb-open");
        return;
      }
      if (isNetworkError(err)) {
        // Shown in place of the welcome, but the panel stays closed and the
        // composer stays live: on a real site this is most likely a passing
        // outage, and a visitor who opens the chat later may find it back.
        showUnreachableError();
        return;
      }
      // Anything else keeps the old behaviour: default styling and a
      // welcome, so an unexpected server error is not alarming.
      void restoreHistoryOrWelcome(defaultWelcome(DEFAULT_ASSISTANT_NAME));
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
